export const refreshResources = {
  myTasks: 'myTasks', voicemail: 'voicemail', myAttendance: 'myAttendance', tickets: 'tickets',
  // 员工端 Owner Activity：业主留言 / 工单报告事件 / 留言已读（后端 2026-10-30 起认这个资源）
  ownerActivity: 'ownerActivity',
} as const
export type RefreshResource = keyof typeof refreshResources
/**
 * 后端可能还不认识的新资源。旧后端收到不认识的资源会以 1008「Invalid subscription」关掉整条连接，
 * 连带 myTasks / voicemail 都收不到——被拒时去掉这些再订阅一次（见 refreshConnection）。
 */
export const optionalRefreshResources: readonly RefreshResource[] = ['tickets', 'ownerActivity']
type Reason = 'change' | 'reconnect' | 'resume' | 'reconcile' | 'local-write'
type Entry = {
  resource: RefreshResource; refresh: () => Promise<unknown> | void
  dirty: boolean; inFlight: boolean; due: number; reason: Reason
  lastSuccess?: number; error?: string
}
const RECONCILE_MS = 300_000

/** Owns refresh timing only. Feature hooks retain their data and HTTP contracts. */
export class RefreshCoordinator {
  private entries = new Set<Entry>()
  private timer?: ReturnType<typeof setTimeout>
  private scheduledAt = Infinity
  private suspended = false
  constructor(private available: () => boolean) {}

  subscribe(resource: RefreshResource, refresh: Entry['refresh']) {
    const entry: Entry = { resource, refresh, dirty: false, inFlight: false,
      due: Date.now() + RECONCILE_MS, reason: 'reconcile' }
    this.entries.add(entry)
    this.schedule()
    return () => { this.entries.delete(entry); this.schedule() }
  }

  invalidate(resources?: readonly RefreshResource[], reason: Reason = 'change') {
    for (const entry of this.entries) {
      if (!resources || resources.includes(entry.resource)) {
        entry.dirty = true
        entry.reason = reason
      }
    }
    this.schedule()
  }

  suspend() { this.suspended = true; clearTimeout(this.timer); this.scheduledAt = Infinity }
  resume() { this.suspended = false; this.invalidate(undefined, 'resume') }
  snapshot() {
    return [...this.entries].map(({ refresh: _refresh, ...status }) => ({ ...status }))
  }

  private schedule() {
    if (this.suspended || !this.entries.size) {
      clearTimeout(this.timer); this.scheduledAt = Infinity
      return
    }
    const ready = this.available()
    const times = [...this.entries].filter(e => !e.inFlight)
      .map(e => ready && e.dirty ? 250 : Math.max(250, e.due - Date.now()))
    if (!times.length) { clearTimeout(this.timer); this.scheduledAt = Infinity; return }
    const delay = ready ? Math.min(...times) : RECONCILE_MS
    const at = Date.now() + delay
    if (at >= this.scheduledAt) return
    clearTimeout(this.timer)
    this.scheduledAt = at
    this.timer = setTimeout(() => { this.scheduledAt = Infinity; this.tick() }, delay)
  }

  private tick() {
    if (!this.suspended && this.available()) {
      for (const entry of this.entries) {
        if (entry.inFlight || (!entry.dirty && entry.due > Date.now())) continue
        if (!entry.dirty) entry.reason = 'reconcile'
        entry.dirty = false
        entry.inFlight = true
        void Promise.resolve().then(() => {
          if (!this.suspended && this.entries.has(entry) && this.available()) return entry.refresh()
        }).then((result) => {
          if (result === false) { entry.error = 'Refresh reported a read failure'; return }
          entry.lastSuccess = Date.now(); entry.error = undefined
        }, (error: unknown) => {
          entry.error = error instanceof Error ? error.message : 'Refresh failed'
        }).finally(() => {
          entry.inFlight = false
          entry.due = Date.now() + RECONCILE_MS + Math.random() * 15_000
          this.schedule()
        })
      }
    }
    this.schedule()
  }
}

export const refreshCoordinator = new RefreshCoordinator(
  () => navigator.onLine && document.visibilityState === 'visible',
)
